import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Activity, Droplet, GlassWater, Moon, Dumbbell, Scale, Sparkles, AlertCircle, Flame, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const ratingMeta = {
  Excellent: { color: "emerald", desc: "You're doing great. Keep it up!" },
  Good: { color: "emerald", desc: "Solid habits — a little tuning and you'll be excellent." },
  Moderate: { color: "amber", desc: "Some metrics need attention. Small changes go a long way." },
  "Needs Attention": { color: "rose", desc: "Several markers are off. Consider reviewing with your doctor." },
};

const ACTIVITY_LABEL = {
  walking: "Walking", running: "Running", yoga: "Yoga",
  gym: "Gym", cycling: "Cycling", swimming: "Swimming",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const load = async () => {
    try { const { data } = await api.get("/dashboard"); setData(data); }
    catch (e) { /* noop */ }
  };

  useEffect(() => { load(); }, [location.key]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (!data) {
    return <div className="p-6 text-slate-500" data-testid="dashboard-loading">Loading dashboard…</div>;
  }

  const meta = ratingMeta[data.rating] || ratingMeta.Good;
  const ringPct = data.score;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-6">
        <div>
          <p className="text-xs uppercase font-bold tracking-wider text-slate-400">Today</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Hello, {user?.name?.split(" ")[0] || "friend"}</h1>
          <p className="text-slate-500 text-sm mt-1">Here's your daily health snapshot.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 stagger">
        {/* Health Score */}
        <div className="lg:col-span-1 bg-white rounded-2xl border border-slate-200 p-6" data-testid="health-score-card">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase font-bold tracking-wider text-slate-400">Health Score</span>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-${meta.color}-50 text-${meta.color}-700 border border-${meta.color}-200`}>{data.rating}</span>
          </div>
          <div className="mt-4 flex items-center gap-5">
            <ScoreRing value={ringPct} color={meta.color} />
            <div>
              <div className="text-4xl font-bold text-slate-900">{data.score}<span className="text-slate-400 text-lg font-medium">/100</span></div>
              <p className="text-sm text-slate-500 mt-1 leading-snug">{meta.desc}</p>
            </div>
          </div>
          {data.notes?.length > 0 && (
            <div className="mt-5 space-y-2">
              {data.notes.slice(0, 3).map((n, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <AlertCircle className="w-4 h-4 mt-0.5 text-amber-500" />
                  <span>{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard testid="card-sugar" icon={Droplet} title="Sugar" value={data.latest_sugar ? `${data.latest_sugar.value}` : "—"} unit={data.latest_sugar ? "mg/dL" : ""} subtitle={data.latest_sugar?.type?.replace(/_/g, " ") || "No reading yet"} onClick={() => navigate("/sugar")} accent="rose" />
          <StatCard testid="card-bp" icon={Activity} title="Blood Pressure" value={data.latest_bp ? `${data.latest_bp.systolic}/${data.latest_bp.diastolic}` : "—"} unit={data.latest_bp ? "mmHg" : ""} subtitle={data.latest_bp ? `Pulse ${data.latest_bp.pulse || "-"}` : "No reading yet"} onClick={() => navigate("/bp")} accent="blue" />
          <StatCard testid="card-weight" icon={Scale} title="Weight" value={data.latest_weight ? `${data.latest_weight.weight_kg}` : "—"} unit={data.latest_weight ? "kg" : ""} subtitle={data.bmi ? `BMI ${data.bmi}` : "No reading yet"} onClick={() => navigate("/weight")} accent="emerald" />
          <StatCard testid="card-water" icon={GlassWater} title="Water" value={`${Math.round((data.water_today_ml || 0) / 100) / 10}`} unit="L" subtitle={`Goal ${(data.water_goal_ml/1000).toFixed(1)}L`} onClick={() => navigate("/water")} accent="blue" />
          <StatCard testid="card-sleep" icon={Moon} title="Sleep" value={data.sleep_latest ? `${data.sleep_latest.hours}` : "—"} unit={data.sleep_latest ? "h" : ""} subtitle={data.sleep_latest ? "Last night" : "No log yet"} onClick={() => navigate("/sleep")} accent="violet" />
          <StatCard testid="card-exercise" icon={Dumbbell} title="Exercise" value={`${data.exercise_minutes_today || 0}`} unit="min" subtitle={`${data.exercise_today_count} session${data.exercise_today_count === 1 ? "" : "s"} • ${data.exercise_calories_today || 0} kcal`} onClick={() => navigate("/exercise")} accent="amber" />
        </div>
      </div>

      {/* Exercise details */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5 md:p-6" data-testid="exercise-detail">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 grid place-items-center"><Dumbbell className="w-5 h-5" /></div>
            <div>
              <h3 className="font-heading font-semibold text-slate-900">Exercise</h3>
              <p className="text-xs text-slate-500">Your recent activity sessions</p>
            </div>
          </div>
          <button onClick={() => navigate("/exercise")} data-testid="exercise-detail-cta" className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
            View all <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <MiniStat label="Minutes today" value={data.exercise_minutes_today || 0} accent="amber" />
          <MiniStat label="Calories today" value={data.exercise_calories_today || 0} accent="rose" icon={Flame} />
          <MiniStat label="Sessions this week" value={data.exercise_week_count || 0} accent="emerald" />
        </div>

        {data.exercise_recent?.length > 0 ? (
          <ul className="divide-y divide-slate-100" data-testid="exercise-recent-list">
            {data.exercise_recent.map((s, i) => {
              const dateLabel = (() => {
                if (!s.date) return "";
                const today = new Date().toISOString().slice(0, 10);
                if (s.date === today) return "Today";
                const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
                if (s.date === y) return "Yesterday";
                return new Date(s.date).toLocaleDateString(undefined, { month: "short", day: "numeric" });
              })();
              return (
                <li key={i} className="flex items-center justify-between py-2.5" data-testid={`exercise-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 grid place-items-center shrink-0"><Dumbbell className="w-4 h-4" /></div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{ACTIVITY_LABEL[s.activity] || s.activity}</div>
                      <div className="text-xs text-slate-500 truncate">{dateLabel} • {s.duration_min} min</div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0">{s.calories} kcal</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl" data-testid="exercise-empty">
            <Dumbbell className="w-6 h-6 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No exercise logged yet.</p>
            <button onClick={() => navigate("/exercise")} data-testid="exercise-empty-cta" className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700">Log your first session →</button>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 stagger">
        <button onClick={() => navigate("/assistant")} data-testid="ai-cta" className="text-left bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 hover:shadow-md hover:border-slate-300 transition">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center"><Sparkles className="w-6 h-6" /></div>
          <div>
            <div className="font-heading font-semibold text-slate-900">Ask the AI assistant</div>
            <div className="text-sm text-slate-500">Get personalized insights powered by Claude.</div>
          </div>
        </button>
        <button onClick={() => navigate("/medication")} data-testid="meds-cta" className="text-left bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 hover:shadow-md hover:border-slate-300 transition">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center"><Activity className="w-6 h-6" /></div>
          <div>
            <div className="font-heading font-semibold text-slate-900">Track medications</div>
            <div className="text-sm text-slate-500">Log doses, never miss your routine.</div>
          </div>
        </button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, title, value, unit, subtitle, onClick, accent = "blue", testid }) {
  const bg = { blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600", rose: "bg-rose-50 text-rose-600", amber: "bg-amber-50 text-amber-600", violet: "bg-violet-50 text-violet-600" }[accent];
  return (
    <button onClick={onClick} data-testid={testid} className="text-left bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md hover:border-slate-300 transition">
      <div className={`w-10 h-10 rounded-full ${bg} grid place-items-center mb-3`}><Icon className="w-5 h-5" /></div>
      <div className="text-xs uppercase font-bold tracking-wider text-slate-400">{title}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5">{value}<span className="text-slate-400 text-sm font-medium ml-1">{unit}</span></div>
      <div className="text-xs text-slate-500 mt-1 capitalize truncate">{subtitle}</div>
    </button>
  );
}

function MiniStat({ label, value, accent = "blue", icon: Icon }) {
  const color = { blue: "text-blue-700", emerald: "text-emerald-700", rose: "text-rose-700", amber: "text-amber-700" }[accent];
  return (
    <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${color} flex items-center gap-1`}>
        {Icon ? <Icon className="w-4 h-4" /> : null}{value}
      </div>
    </div>
  );
}

function ScoreRing({ value, color = "emerald" }) {
  const radius = 36; const c = 2 * Math.PI * radius;
  const offset = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  const stroke = { emerald: "#10b981", amber: "#f59e0b", rose: "#f43f5e" }[color] || "#10b981";
  return (
    <svg width="92" height="92" viewBox="0 0 92 92">
      <circle cx="46" cy="46" r={radius} stroke="#e2e8f0" strokeWidth="8" fill="none" />
      <circle cx="46" cy="46" r={radius} stroke={stroke} strokeWidth="8" fill="none"
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 46 46)" style={{ transition: "stroke-dashoffset 800ms ease" }} />
    </svg>
  );
}
